import { useEffect, useState } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";

type StickyHeaderProgressProps = {
  step: number;
  total: number;
  onBack?: () => void;
  canGoBack?: boolean;
};

export function StickyHeaderProgress({
  step,
  total,
  onBack,
  canGoBack,
}: StickyHeaderProgressProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const ENTER_COMPACT_AT = 72;
    const EXIT_COMPACT_AT = 28;

    const onScroll = () => {
      const y = window.scrollY;
      setScrolled((prev) => {
        if (!prev && y >= ENTER_COMPACT_AT) return true;
        if (prev && y <= EXIT_COMPACT_AT) return false;
        return prev;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`
        sticky top-0 z-40 mb-5 -mx-4 sm:mx-0
        transition-[background-color,backdrop-filter,border-color,box-shadow] duration-300 ease-in-out
        ${
          scrolled
            ? "bg-[#fff9f5]/75 backdrop-blur border-b border-[#f2dfd8] shadow-[0_4px_16px_rgba(15,23,42,0.05)]"
            : "bg-transparent"
        }
      `}
    >
      <div
        className={`
          relative flex items-center px-4 sm:px-0
          transition-[height,opacity] duration-300 ease-in-out
          ${scrolled ? "h-14" : "h-16"}
        `}
      >
        {canGoBack && onBack && step > 0 && (
          <button
            onClick={onBack}
            className="
              inline-flex items-center gap-1 rounded-full border border-[var(--line)]
              bg-[#ffffffd9] px-3 py-1.5 text-sm font-semibold text-[var(--text-muted)]
              hover:text-[var(--text-primary)] hover:border-gray-300
              transition
              focus:outline-none
              focus:ring-2 focus:ring-[var(--brand)]
            "
          >
            <ArrowLeft size={16} />
            Back
          </button>
        )}

        <div
          className={`
            absolute left-1/2 -translate-x-1/2
            transition-opacity duration-300 ease-in-out
            ${scrolled ? "opacity-100" : "opacity-100"}
          `}
        >
          <img
            src="/site-logo.svg"
            alt="Baker Home Energy"
            className={`
              h-10 origin-center transition-transform duration-300 ease-in-out
              ${scrolled ? "scale-[0.88] -translate-y-[1px]" : "scale-100 translate-y-0"}
            `}
          />
        </div>

        <div
          className={`
            ml-auto flex items-center gap-2 transition-opacity duration-300 ease-in-out
            ${scrolled ? "opacity-100" : "opacity-100"}
          `}
        >
          <ShieldCheck size={14} className="text-[var(--brand)]" />
          <span className="inline-flex shrink-0 rounded-full bg-[var(--brand-soft)] px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] text-[var(--text-muted)] sm:text-[10px]">
            <span className="text-center leading-[1.05] sm:hidden">
              SECURE
              <br />
              ESTIMATE
            </span>
            <span className="hidden whitespace-nowrap sm:inline">SECURE ESTIMATE</span>
          </span>
        </div>
      </div>

      <div
        className={`
          px-4 sm:px-0
          transition-[padding,opacity] duration-300 ease-in-out
          ${scrolled ? "pb-3 pt-0.5" : "pb-4 pt-1"}
        `}
      >
        <div className="flex gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={`
                flex-1 rounded-full transition-[height,background-color] duration-300
                ${scrolled ? "h-1.5" : "h-2"}
                ${i <= step ? "bg-[var(--brand)]" : "bg-gray-200"}
              `}
            />
          ))}
        </div>

        <div className="mt-2 flex items-center justify-between">
          <p
            className={`
              text-[11px] font-medium text-[var(--text-muted)]
              transition-all duration-300
              ${scrolled ? "opacity-80" : "opacity-100"}
            `}
          >
            Step {step + 1} of {total}
          </p>
          <p className="rounded-full bg-[var(--brand-soft)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--brand)]">
            60-second estimate
          </p>
        </div>
      </div>
    </div>
  );
}
